import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

interface CreateFlowDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: { name: string; slug: string; description?: string }) => void;
  isSubmitting: boolean;
  error?: string;
}

/**
 * Generate a URL-safe slug from a name
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, "") // Remove non-URL-safe characters
    .replace(/-+/g, "-") // Replace multiple hyphens with single
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
}

export function CreateFlowDialog({
  open,
  onCancel,
  onSave,
  isSubmitting,
  error,
}: CreateFlowDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");

  // Track if user has manually edited the slug
  const slugManuallyEdited = useRef(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setDescription("");
      slugManuallyEdited.current = false;
    }
  }, [open]);

  // Auto-generate slug from name (unless manually edited)
  const handleNameChange = (newName: string) => {
    setName(newName);
    if (!slugManuallyEdited.current) {
      setSlug(generateSlug(newName));
    }
  };

  // Track manual slug edits
  const handleSlugChange = (newSlug: string) => {
    slugManuallyEdited.current = true;
    setSlug(newSlug);
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const trimmedSlug = slug.trim();

    if (!trimmedName || !trimmedSlug) return;

    onSave({
      name: trimmedName,
      slug: trimmedSlug,
      description: description.trim() || undefined,
    });
  };

  const canSubmit = name.trim() && slug.trim() && !isSubmitting;

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={onCancel}
        onPointerDownOutside={onCancel}
      >
        <DialogHeader>
          <DialogTitle>Create New Flow</DialogTitle>
          <DialogDescription>
            Create a new flow to define user journeys through your interaction
            modules.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <label className="text-xs font-medium">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="My Onboarding Flow"
              className="text-sm"
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">
              Slug <span className="text-destructive">*</span>
            </label>
            <Input
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="my-onboarding-flow"
              className="text-sm font-mono"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              URL-safe identifier. Auto-generated from name.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description of what this flow does"
              className="text-sm h-20"
              disabled={isSubmitting}
            />
          </div>
          {error && (
            <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            size="sm"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} size="sm" disabled={!canSubmit}>
            {isSubmitting ? "Creating..." : "Create Flow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
